// Configuración correcta de API
const API_BASE_URL = '/api';
import { ipService } from './ipService';

// Tipos actualizados para compatibilidad completa
export interface SearchParams {
  query: string;
  language?: string;
  filters?: {
    category?: string;
    dateRange?: {
      from?: string;
      to?: string;
    }
  }
}

export interface Section {
  title: string;
  type: string;
  content?: Record<string, any>;
  items?: string[];
  data?: string[][];
}

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  partNumber?: string;
  relevance: number;
  sections: Section[];
  type: string;
  source: string;
  timestamp: string;
}

export interface SearchResponse {
  results: SearchResult[];
  metadata: {
    totalResults: number;
    processingTime: number;
    type: 'technical' | 'general';
    confidence: number;
    query?: string;
    timestamp?: string;
    totalSections?: number;
  }
}

export interface TechnicalResponse {
  answer: string;
  timestamp: string;
  hasContext: boolean;
  confidence: number;
  
  // ✅ CAMPOS DEL BACKEND
  metadata?: any;
  sources?: string[];
  data_source?: string;
  processingTime?: number;
  success?: boolean;
  found?: number;
  method?: string;
  
  // ✅ ESTRUCTURA COMPLETA DEL BACKEND
  result?: any;
  analysis?: any;
  sessionId?: string;
  memoryEnabled?: boolean;
  contextUsed?: boolean;
  debug?: any;
}

export interface HealthResponse {
  status: string;
  timestamp: Date | string;
  services?: {
    api: string;
    database: string;
    sis2: string;
  };
  service?: string;
  error?: string;
}

console.log('API Base URL configurada:', API_BASE_URL);

// Configuración de timeouts y reintentos
const REQUEST_TIMEOUT = 150000;// ✅ AUMENTADO a 90 segundos para SISWeb
const MAX_RETRIES = 2;         // ✅ Reducido para evitar reintentos largos
const RETRY_DELAY = 3000;      // ✅ Aumentado ligeramente

// Clase de error personalizada
export class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number = 0, data: any = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

// Headers comunes para las peticiones
const getHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
});

// ✅ Función con backoff exponencial
async function makeRequest<T>(
  url: string, 
  options: RequestInit = {}, 
  retries: number = MAX_RETRIES
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    console.log(`Realizando petición: ${options.method || 'GET'} ${url}`);
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...getHeaders(),
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = `Error HTTP ${response.status}: ${response.statusText}`;
      let errorData = null;

      try {
        errorData = await response.text();
        const jsonError = JSON.parse(errorData);
        errorMessage = jsonError.message || jsonError.error || errorMessage;
      } catch (parseError) {
        if (errorData) {
          errorMessage = errorData;
        }
      }

      // ✅ NO reintentar en errores 4xx (incluyendo 429 - Too Many Requests)
      if (response.status >= 400 && response.status < 500) {
        throw new APIError(errorMessage, response.status, errorData);
      }

      throw new APIError(errorMessage, response.status, errorData);
    }

    const data = await response.json();
    //console.log('Respuesta recibida:', data);
    return data;

  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new APIError('La petición ha excedido el tiempo límite', 408);
    }

    if (error instanceof APIError) {
      // ✅ NO reintentar errores 4xx
      if (error.status >= 400 && error.status < 500) {
        throw error;
      }
    }

    // ✅ Error de red con backoff exponencial
    if (error instanceof TypeError && error.message.includes('fetch')) {
      if (retries > 0) {
        const backoffDelay = RETRY_DELAY * Math.pow(2, MAX_RETRIES - retries); // Backoff exponencial
        console.warn(`Error de conexión, reintentando en ${backoffDelay}ms... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return makeRequest<T>(url, options, retries - 1);
      }
      throw new APIError('Error de conexión. Verifique su conexión a internet.', 0);
    }

    throw new APIError(error.message || 'Error desconocido', 0);
  }
}

// Función para realizar búsquedas
export const searchData = async (params: SearchParams): Promise<SearchResponse> => {
  try {
    if (!params.query || !params.query.trim()) {
      throw new APIError('La consulta de búsqueda es requerida', 400);
    }

    const requestBody = {
      query: params.query.trim(),
      language: params.language || 'es',
      filters: params.filters || {}
    };

    console.log('Parámetros de búsqueda:', requestBody);

    const response = await makeRequest<SearchResponse>(`${API_BASE_URL}/search`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });

    // Validar estructura de respuesta
    if (!response || !Array.isArray(response.results)) {
      throw new APIError('Respuesta inválida del servidor', 502);
    }

    // Procesar y validar resultados
    const processedResponse: SearchResponse = {
      results: response.results.map(result => ({
        id: result.id || `result-${Date.now()}-${Math.random()}`,
        title: result.title || 'Sin título',
        description: result.description || 'Sin descripción disponible',
        category: result.category || 'general',
        partNumber: result.partNumber || undefined,
        relevance: Math.max(0, Math.min(1, Number(result.relevance) || 0)),
        sections: Array.isArray(result.sections) ? result.sections : [],
        type: result.type || 'general',
        source: result.source || 'SIS2',
        timestamp: result.timestamp || new Date().toISOString()
      })),
      metadata: {
        totalResults: response.metadata?.totalResults || response.results.length,
        processingTime: response.metadata?.processingTime || 0,
        type: response.metadata?.type || 'general',
        confidence: Math.max(0, Math.min(1, Number(response.metadata?.confidence) || 0.5)),
        query: params.query,
        timestamp: new Date().toISOString(),
        ...response.metadata
      }
    };

    console.log('Búsqueda completada exitosamente:', processedResponse);
    return processedResponse;

  } catch (error) {
    console.error('Error en searchData:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    throw new APIError(
      error.message || 'Error al realizar la búsqueda',
      error.status || 500
    );
  }
};

let lastAIRequest = 0;
const AI_REQUEST_DELAY = 1000; // 2 segundos entre requests de IA

// ✅ NUEVA FUNCIÓN PARA OBTENER INFORMACIÓN DEL USUARIO
export const getCurrentUser = async (): Promise<any> => {
  try {
    const token = localStorage.getItem('kikai_token');
    if (!token) {
      throw new Error('No token found');
    }

    const response = await fetch('/api/user/profile', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get user profile');
    }

    const userData = await response.json();
    console.log('👤 Usuario obtenido:', {
      id: userData.id,
      email: userData.email,
      subscription_plan: userData.subscription_plan
    });
    
    return userData;
    
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// helpers (puedes moverlo al top del archivo)
const normalizeQuestionToString = (q: unknown): string => {
  if (typeof q === 'string') return q;
  if (q == null) return '';
  const anyQ = q as any;
  // campos típicos que pueden venir desde botones/metadatos
  if (typeof anyQ?.text === 'string') return anyQ.text;
  if (typeof anyQ?.query === 'string') return anyQ.query;
  if (typeof anyQ?.label === 'string') return anyQ.label;
  if (typeof anyQ?.payload === 'string') return anyQ.payload;
  try { return JSON.stringify(q); } catch { return String(q); }
};



// Función para realizar consultas técnicas con IA
export const askTechnicalQuestion = async (
  questionInput: unknown,
  options: any = {}
): Promise<TechnicalResponse> => {
  try {
     // ✅ OBTENER IP PÚBLICA
    const publicIp = await ipService.getPublicIP();
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      publicIp: publicIp
    };
    const question = normalizeQuestionToString(questionInput);
    const preview = question.slice(0, 50); // más seguro que llamar substring sobre algo no-string

    console.log('🔵 askTechnicalQuestion iniciando:', { question: preview, options });

    if (!question.trim()) {
      throw new APIError('La pregunta es requerida', 400);
    }

    // ✅ Delay reducido entre requests de IA
    const timeSinceLastRequest = Date.now() - lastAIRequest;
    if (timeSinceLastRequest < 1000) {
      const waitTime = 1000 - timeSinceLastRequest;
      console.log(`⏳ Esperando ${waitTime}ms antes del siguiente request...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    lastAIRequest = Date.now();


    // ✅ CONSTRUCCIÓN MEJORADA DEL REQUEST BODY
    const requestBody = {
      question: question.trim(),
      use_knowledge_base: true,
      context: options.context || null,
      language: options.language || 'es',
      userId: options.userId || null,
      chatHistory: options.chatHistory || [],
      sessionId: options.sessionId || null,
      deviceInfo: deviceInfo
    };

    console.log('🔵 Request body preparado:', JSON.stringify(requestBody, null, 2));
    console.log('📡 Enviando pregunta técnica:', {
      question: requestBody.question.substring(0, 100),
      sessionId: requestBody.sessionId || 'new',
      hasDeviceInfo: !!deviceInfo.publicIp
    });

    const apiUrl = `${API_BASE_URL}/ask`;
    console.log('🔵 URL objetivo:', apiUrl);
    console.log('⏰ Timeout configurado para 90 segundos - Consultas SISWeb pueden tardar...');

    try {
      // ✅ TIMER DE ADVERTENCIA A LOS 30 SEGUNDOS
      const warningTimer = setTimeout(() => {
        console.log('⚠️ La consulta está tardando más de lo normal. SISWeb está procesando acordeones...');
      }, 60000);

      // ✅ HACER REQUEST CON TIMEOUT EXTENDIDO
      const dynamicTimeout = question.match(/^[A-Z]\d{1,4}$/i) ? 150000 : 60000; // Más tiempo para códigos técnicos
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(dynamicTimeout)
      });

      // ✅ LIMPIAR TIMER DE ADVERTENCIA
      clearTimeout(warningTimer);

      console.log('🔵 Response status:', response.status);
      console.log('🔵 Response headers:', Object.fromEntries(response.headers.entries()));

      // ✅ VERIFICAR CONTENT-TYPE
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('❌ Respuesta no es JSON:', textResponse.substring(0, 200));
        throw new APIError('El servidor no devolvió JSON válido', response.status);
      }

      // ✅ PARSEAR RESPUESTA
      const responseData = await response.json();
      console.log('🔵 Response data keys:', Object.keys(responseData));
      console.log('🔵 Response success:', responseData.success);
      console.log('🔵 Response found:', responseData.found);

      if (!response.ok) {
        const errorMsg = responseData.error || responseData.message || `HTTP ${response.status}`;
        console.error('❌ Error del servidor:', errorMsg);
        throw new APIError(errorMsg, response.status, responseData);
      }

      // ✅ VALIDAR ESTRUCTURA DE RESPUESTA
      if (!responseData) {
        throw new APIError('Respuesta vacía del servidor', 502);
      }

     // 🎯 USAR RESPUESTA DEL BACKEND DIRECTAMENTE SIN SOBRESCRIBIR
    console.log('🔍 [API-SERVICE] Respuesta del backend recibida:', {
      hasResult: !!responseData.result,
      hasAnswer: !!responseData.result?.answer,
      hasMetadata: !!responseData.result?.metadata,
      hasButtons: responseData.result?.metadata?.hasButtons,
      buttonCount: responseData.result?.metadata?.interactiveButtons?.length || 0,
      dataSource: responseData.debug?.dataSource
    });

 // ✅ DEVOLVER RESPUESTA DEL BACKEND SIN MODIFICACIONES
 const processedResponse = {
   // ✅ MANTENER ESTRUCTURA PARA COMPATIBILIDAD
   answer: responseData.result?.answer || responseData.answer || 'No se recibió respuesta',
   timestamp: responseData.timestamp || new Date().toISOString(),
   hasContext: !!responseData.contextUsed || !!options.context,
   confidence: responseData.result?.confidence || responseData.confidence || 0.8,
   
   // 🎯 CAMPOS CRÍTICOS: USAR DATOS DEL BACKEND, NO SOBRESCRIBIR
   metadata: responseData.result?.metadata || responseData.metadata,
   sources: responseData.result?.sources || responseData.sources || [],
   data_source: responseData.debug?.dataSource || 'sistema_local_kikai', // ✅ DEL BACKEND
   processingTime: responseData.processingTime || 0,
   
   // 🎯 CAMPOS ADICIONALES DEL BACKEND
   success: responseData.success,
   found: responseData.result?.found || responseData.found,
   method: responseData.result?.method || responseData.method,
   
   // 🎯 ESTRUCTURA COMPLETA DEL BACKEND
   result: responseData.result, // ✅ INCLUIR TODO EL RESULT
   analysis: responseData.analysis,
   sessionId: responseData.sessionId,
   memoryEnabled: responseData.memoryEnabled,
   contextUsed: responseData.contextUsed,
   debug: responseData.debug
 };
 
 console.log('✅ [API-SERVICE] Respuesta procesada sin sobrescribir:', {
   hasResult: !!processedResponse.result,
   hasMetadata: !!processedResponse.metadata,
   hasButtons: processedResponse.metadata?.hasButtons,
   buttonCount: processedResponse.metadata?.interactiveButtons?.length || 0,
   dataSource: processedResponse.data_source
 });
 
 return processedResponse;

    } catch (fetchError) {
      console.error('❌ Error en fetch:', fetchError);
      
      // ✅ MANEJO MEJORADO DE TIMEOUTS
      if (fetchError.name === 'AbortError' || fetchError.message.includes('timed out')) {
        throw new APIError(
          'La consulta SISWeb tardó más de 90 segundos. Esto puede ocurrir con consultas complejas. ' +
          'Por favor, intenta con una consulta más específica o inténtalo nuevamente.', 
          408
        );
      }
      
      // ✅ Si es error de red, crear respuesta de fallback
      if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
        console.log('🔄 Creando respuesta de fallback por error de red...');
        
        return {
          answer: 'Lo siento, hay un problema de conexión temporal. Por favor, intenta de nuevo en unos momentos.',
          timestamp: new Date().toISOString(),
          hasContext: false,
          confidence: 0.1,
          sources: ['fallback'],
          data_source: 'fallback_response'
        };
      }
      
      throw fetchError;
    }

  } catch (error) {
    console.error('❌ Error general en askTechnicalQuestion:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    // ✅ RESPUESTA DE EMERGENCIA PARA ERRORES NO MANEJADOS
    return {
      answer: 'Disculpa, hubo un error procesando tu consulta. El equipo técnico ha sido notificado.',
      timestamp: new Date().toISOString(),
      hasContext: false,
      confidence: 0.1,
      sources: ['error_fallback'],
      data_source: 'error_response',
      error: error.message
    };
  }
};


// Función para obtener detalles de un item
export const getItemDetails = async (id: string): Promise<any> => {
  try {
    if (!id) {
      throw new APIError('ID del item es requerido', 400);
    }

    console.log('Obteniendo detalles del item:', id);

    const response = await makeRequest(`${API_BASE_URL}/items/${encodeURIComponent(id)}`);
    
    console.log('Detalles del item obtenidos exitosamente');
    return response;

  } catch (error) {
    console.error('Error en getItemDetails:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    throw new APIError(
      error.message || 'Error al obtener detalles del item',
      error.status || 500
    );
  }
};


// Interfaces para categorías jerárquicas
export interface SubcategoryItem {
  id: number;
  item_name: string;
  part_number: string;
  serial_range?: string;
  specifications: Record<string, any>;
  image_url?: string;
}

export interface Subcategory {
  id: number;
  subcategory_name: string;
  description?: string;
  total_items: number;
  items: SubcategoryItem[];
}

export interface CategoryDetails {
  category: {
    id: string;
    name: string;
    description?: string;
    tabName: string;
    totalItems: number;
    equipment: {
      serialNumber: string;
      model: string;
    };
  };
  subcategories: Subcategory[];
  totalSubcategories: number;
  totalItems: number;
  timestamp: string;
  hierarchical: boolean;
}

// ✅ NUEVA FUNCIÓN PARA CARGAR DETALLES DE CATEGORÍA
export const getCategoryDetails = async (categoryId: string): Promise<CategoryDetails> => {
  try {
    if (!categoryId) {
      throw new APIError('ID de categoría es requerido', 400);
    }

    console.log('Obteniendo detalles de categoría:', categoryId);

    const response = await makeRequest<CategoryDetails>(
      `${API_BASE_URL}/category/${encodeURIComponent(categoryId)}/details`
    );
    
    // Validar estructura de respuesta
    if (!response || !response.category) {
      throw new APIError('Respuesta inválida del servidor para categoría', 502);
    }

    console.log('Detalles de categoría obtenidos exitosamente:', {
      categoryName: response.category.name,
      subcategoriesCount: response.subcategories?.length || 0,
      totalItems: response.totalItems || 0
    });

    return response;

  } catch (error) {
    console.error('Error en getCategoryDetails:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    throw new APIError(
      error.message || 'Error al obtener detalles de la categoría',
      error.status || 500
    );
  }
};

// Función para verificar el estado del sistema
export const checkSystemHealth = async (): Promise<HealthResponse> => {
  try {
    console.log('Verificando estado del sistema...');

    const response = await makeRequest<HealthResponse>(`${API_BASE_URL}/health`);
    
    console.log('Estado del sistema verificado');
    return response;

  } catch (error) {
    console.error('Error en checkSystemHealth:', error);
    
    // Devolver estado de error en lugar de lanzar excepción
    return {
      status: 'error',
      timestamp: new Date().toISOString(),
      error: error.message || 'Error al verificar estado del sistema'
    };
  }
};

// Función para verificar el estado del servicio de IA
export const checkAIHealth = async (): Promise<HealthResponse> => {
  try {
    console.log('Verificando estado del servicio de IA...');

    const response = await makeRequest<HealthResponse>(`${API_BASE_URL}/health/ai`);
    
    console.log('Estado del servicio de IA verificado');
    return response;

  } catch (error) {
    console.error('Error en checkAIHealth:', error);
    
    // Devolver estado de error en lugar de lanzar excepción
    return {
      status: 'error',
      service: 'ai',
      timestamp: new Date().toISOString(),
      error: error.message || 'Error al verificar estado del servicio de IA'
    };
  }
};

// Función utilitaria para verificar si el servicio está disponible
export const isServiceAvailable = async (): Promise<boolean> => {
  try {
    const health = await checkSystemHealth();
    return health.status === 'ok' || health.status === 'healthy';
  } catch (error) {
    console.warn('Servicio no disponible:', error);
    return false;
  }
};

// ✅ AGREGAR MÉTODO DELETE AL FINAL DE apiService.js
export const deleteRequest = async (endpoint) => {
  console.log(`🗑️ API Service - DELETE:`, endpoint);
  
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    // Verificar si hay contenido en la respuesta
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log('✅ API Service - DELETE exitoso:', endpoint);
      return data;
    } else {
      // Respuesta sin contenido JSON (común en DELETE)
      console.log('✅ API Service - DELETE exitoso (sin contenido):', endpoint);
      return { success: true };
    }
    
  } catch (error) {
    console.error('❌ API Service - Error en DELETE:', error);
    throw error;
  }
};


// Función para obtener información de configuración
// Función para obtener información de configuración
export const getConfig = () => ({
  apiBaseUrl: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  maxRetries: MAX_RETRIES,
  retryDelay: RETRY_DELAY
});

// ✅ EXPORTACIÓN ACTUALIZADA CON DELETE
export default {
  searchData,
  askTechnicalQuestion,
  getItemDetails,
  getCategoryDetails,
  checkSystemHealth,
  checkAIHealth,
  isServiceAvailable,
  getConfig,
  delete: deleteRequest,  // ← AGREGAR ESTA LÍNEA
  APIError
};
